import { useCallback, useEffect, useRef, useState } from 'react'
import { api, ErroApi, type Lista, type Paciente } from '../lib/api'
import { useSessao } from '../lib/sessao'
import { FichaPaciente } from './FichaPaciente'
import './pacientes.css'

/**
 * Lista de pacientes.
 *
 * A busca casa nome, telefone e CPF com os mesmos dígitos: a recepção
 * digita o telefone do jeito que o paciente fala, com parêntese e
 * traço, e ainda assim tem de encontrar.
 */

const SELO: Record<Paciente['financeiro'], { classe: string; rotulo: string }> = {
  OK:       { classe: 'selo--ok',      rotulo: 'em dia' },
  PENDENTE: { classe: 'selo--atencao', rotulo: 'pendente' },
  VENCIDO:  { classe: 'selo--erro',    rotulo: 'vencido' },
}

const soDigitos = (v: string) => v.replace(/\D/g, '')

/** Mostra o telefone como gente lê, não como o banco guarda. */
function telefoneBonito(e164: string | null): string {
  if (!e164) return '—'
  const d = e164.replace(/\D/g, '')
  const nac = d.startsWith('55') ? d.slice(2) : d
  if (nac.length === 11) return `(${nac.slice(0, 2)}) ${nac.slice(2, 7)}-${nac.slice(7)}`
  if (nac.length === 10) return `(${nac.slice(0, 2)}) ${nac.slice(2, 6)}-${nac.slice(6)}`
  return e164
}

const cpfBonito = (c: string | null) =>
  c && c.length === 11 ? `${c.slice(0,3)}.${c.slice(3,6)}.${c.slice(6,9)}-${c.slice(9)}` : '—'

export function Pacientes() {
  const { pode } = useSessao()
  const [busca, setBusca] = useState('')
  const [itens, setItens] = useState<Paciente[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [novo, setNovo] = useState(false)
  const [aberto, setAberto] = useState<string | null>(null)
  const timer = useRef<number | null>(null)

  const buscar = useCallback(async (termo: string, sinal?: AbortSignal) => {
    setErro(null)
    try {
      const q = new URLSearchParams({ limite: '50' })
      if (termo.trim()) q.set('busca', termo.trim())
      const r = await api<Lista<Paciente>>(`/v1/pacientes?${q}`, sinal ? { sinal } : {})
      setItens(r.itens)
    } catch (e) {
      if ((e as Error).name === 'AbortError') return
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível carregar.')
    } finally {
      setCarregando(false)
    }
  }, [])

  // Debounce: sem ele, a cada tecla sai uma requisição, e digitar um
  // nome de dez letras vira dez consultas concorrentes.
  useEffect(() => {
    const c = new AbortController()
    if (timer.current) window.clearTimeout(timer.current)
    setCarregando(true)
    timer.current = window.setTimeout(() => void buscar(busca, c.signal), 280)
    return () => { c.abort(); if (timer.current) window.clearTimeout(timer.current) }
  }, [busca, buscar])

  // A ficha ocupa a tela inteira: é onde a conversa com o paciente
  // acontece, e dividir espaço com a lista só tira leitura de ambos.
  if (aberto) {
    return <FichaPaciente pacienteId={aberto} aoVoltar={() => { setAberto(null); void buscar(busca) }} />
  }

  return (
    <div className="pacientes">
      <header className="pacientes__topo">
        <h1>Pacientes</h1>
        {pode('paciente:write') && (
          <button className="botao" onClick={() => setNovo(true)}>Novo paciente</button>
        )}
      </header>

      <div className="campo">
        <label htmlFor="busca" className="oculto-visual">Buscar paciente</label>
        <input id="busca" className="entrada" type="search"
               placeholder="Nome, telefone, CPF ou e-mail"
               value={busca} onChange={(e) => setBusca(e.target.value)} />
      </div>

      {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

      <div className="rolagem-x">
        <table className="tabela">
          <thead>
            <tr>
              <th>Nome</th><th>Telefone</th><th>CPF</th><th>Financeiro</th>
            </tr>
          </thead>
          <tbody>
            {carregando && Array.from({ length: 4 }, (_, i) => (
              <tr key={`e${i}`}>
                <td colSpan={4}><div className="esqueleto" style={{ height: 18 }} /></td>
              </tr>
            ))}

            {!carregando && itens.length === 0 && (
              <tr><td colSpan={4} className="tabela__vazio">
                {busca
                  ? `Nenhum paciente encontrado para “${busca}”.`
                  : 'Nenhum paciente cadastrado ainda.'}
              </td></tr>
            )}

            {!carregando && itens.map((p) => (
              <tr key={p.id} className="pacientes__linha"
                  onClick={() => setAberto(p.id)}
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') setAberto(p.id) }}>
                <td>
                  <span className="pacientes__nome">{p.nome_social ?? p.nome}</span>
                  {p.nome_social && <span className="ajuda"> ({p.nome})</span>}
                </td>
                <td className="mono">{telefoneBonito(p.telefone)}</td>
                <td className="mono">{cpfBonito(p.cpf)}</td>
                <td>
                  <span className={`selo ${SELO[p.financeiro].classe}`}>
                    {SELO[p.financeiro].rotulo}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {novo && (
        <NovoPaciente
          aoFechar={() => setNovo(false)}
          aoCriar={() => { setNovo(false); void buscar(busca) }}
        />
      )}
    </div>
  )
}

/* ══ cadastro ═════════════════════════════════════════════════════ */

function NovoPaciente({ aoFechar, aoCriar }: { aoFechar(): void; aoCriar(): void }) {
  const [nome, setNome] = useState('')
  const [telefone, setTelefone] = useState('')
  const [cpf, setCpf] = useState('')
  const [nascimento, setNascimento] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  // O CPF é conferido AQUI só para avisar cedo. Quem realmente recusa
  // é o CHECK do banco — validar só no formulário deixaria a porta
  // aberta para qualquer outro caminho de escrita.
  const cpfDigitos = soDigitos(cpf)
  const cpfIncompleto = cpfDigitos.length > 0 && cpfDigitos.length !== 11
  const telDigitos = soDigitos(telefone)
  const telIncompleto = telDigitos.length > 0 && telDigitos.length < 10

  async function submeter(e: React.FormEvent) {
    e.preventDefault()
    setErro(null); setEnviando(true)
    try {
      await api('/v1/pacientes', {
        metodo: 'POST',
        corpo: {
          nome: nome.trim(),
          ...(telefone ? { telefone } : {}),
          ...(cpf ? { cpf } : {}),
          ...(nascimento ? { nascimento } : {}),
        },
      })
      aoCriar()
    } catch (err) {
      setErro(err instanceof ErroApi ? err.message : 'Não foi possível salvar.')
      setEnviando(false)
    }
  }

  return (
    <>
      <div className="painel__fundo" onClick={aoFechar} />
      <aside className="painel" role="dialog" aria-modal="true" aria-label="Novo paciente">
        <header className="painel__topo">
          <h2>Novo paciente</h2>
          <button className="botao botao--fantasma botao--pequeno" onClick={aoFechar}>
            Fechar
          </button>
        </header>

        <form className="formulario" onSubmit={submeter}>
          {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

          <div className="campo">
            <label htmlFor="p-nome">Nome completo</label>
            <input id="p-nome" className="entrada" required autoFocus
                   value={nome} onChange={(e) => setNome(e.target.value)} />
          </div>

          <div className="campo">
            <label htmlFor="p-tel">Telefone</label>
            <input id="p-tel" className="entrada" type="tel" inputMode="tel"
                   placeholder="(75) 99202-3298"
                   aria-invalid={telIncompleto}
                   value={telefone} onChange={(e) => setTelefone(e.target.value)} />
            <span className={telIncompleto ? 'ajuda ajuda--erro' : 'ajuda'}>
              {telIncompleto
                ? 'Faltam dígitos. Inclua o DDD.'
                : 'Com DDD. É por aqui que a confirmação chega.'}
            </span>
          </div>

          <div className="campo">
            <label htmlFor="p-cpf">CPF</label>
            <input id="p-cpf" className="entrada" inputMode="numeric"
                   placeholder="000.000.000-00"
                   aria-invalid={cpfIncompleto}
                   value={cpf} onChange={(e) => setCpf(e.target.value)} />
            <span className={cpfIncompleto ? 'ajuda ajuda--erro' : 'ajuda'}>
              {cpfIncompleto
                ? `${cpfDigitos.length} de 11 dígitos.`
                : 'Opcional agora; obrigatório para emitir nota.'}
            </span>
          </div>

          <div className="campo">
            <label htmlFor="p-nasc">Nascimento</label>
            <input id="p-nasc" className="entrada" type="date"
                   value={nascimento} onChange={(e) => setNascimento(e.target.value)} />
          </div>

          <button className="botao" type="submit"
                  disabled={enviando || !nome.trim() || cpfIncompleto || telIncompleto}>
            {enviando ? 'Salvando…' : 'Salvar paciente'}
          </button>
        </form>
      </aside>
    </>
  )
}
